import { Divider } from '@katalon-studio/katalon-ui/v2';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { useSnackbar } from 'notistack';
import { parse } from 'querystring';
import { useEffect, useState } from 'react';
import { useCookies } from 'react-cookie';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import uniq from 'lodash/fp/uniq';
import { ReactComponent as MemoIcon } from '../../../components/icons/icon_small_memo-circle-info.svg';
import {
  useCheckDowngrade,
  useGetQuotaAndCycleFromPathParams,
  useGetTrackingCookies,
  usePlanCartSnapshot,
} from '../../../hook';
import { useLaunchDarkly } from '../../../launchdarkly';
import { LoadingProgress } from '../../../layout';
import PricingSection from '../../../layout/pricing-section/PricingSection';
import { confirmPayment, paymentStatus, plan } from '../../../layout/routes';
import { Feature, OrderStatus, PaymentMethodType, PlanInterval, SubscriptionSource } from '../../../models';
import { QueryDictionary, useNavigate, useQuery, useReadOnlyMode } from '../../../routes';
import {
  fromAccounts,
  fromBillingInformation,
  fromOrder,
  fromPaymentMethod,
  fromSubscription,
  useAppDispatch,
} from '../../../store';
import { PARTNER_ID_COOKIE_NAME } from '../../../utils/Constants';
import { MAX_TOTAL_AMOUNT, sendTrackingData } from '../../../utils/utils';
import ConfirmPaymentDialog from '../component/ConfirmPaymentDialog';
import { formatMessageRecurly, getPlanId } from '../utils';
import CreditItem from './CreditItem';
import OrderItem from './OrderItem';
import { TermAndConditionCheck } from './TermAndConditionCheck';
import { withCartSnapshot } from '../../../utils/analytics';
import { createFeatures } from '../../../models/FeatureConfig';

const useStyles = makeStyles(theme => ({
  memoTitle: {
    paddingTop: theme.spacing(2),
  },
  memoTitleText: {
    marginLeft: theme.spacing(1),
    fontSize: 14,
    fontWeight: 500,
    lineHeight: 1.57,
    color: '#1847ca',
  },
  memoText: {
    marginTop: theme.spacing(0.5),
    fontSize: 12,
    fontWeight: 500,
    color: '#727993',
  },
  creditItemContainer: {
    marginTop: 16,
  },
}));

interface OrderConfirmationSectionProps {
  accountId: number;
  handleSave: () => Promise<void>;
  loading: boolean;
  setLoading: Function;
}

const OrderConfirmationSection = (props: OrderConfirmationSectionProps) => {
  const {
    handleSave,
    accountId,
    loading,
    setLoading,
  } = props;

  const classes = useStyles();
  const { get, queryString } = useQuery();
  const { flags } = useLaunchDarkly();
  const intl = useIntl();
  const dispatch = useAppDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const previewLoading = useSelector(fromOrder.selectPreviewLoading);
  const previews = useSelector(fromOrder.selectOrderProductsPreview);
  const previewsTotal = useSelector(fromOrder.selectPreviewsTotal);
  const previewsNetTotal = useSelector(fromOrder.selectPreviewsSubTotal);
  const previewsTotalTax = useSelector(fromOrder.selectPreviewsTotalTax);
  const previewsDiscountTotal = useSelector(fromOrder.selectDiscountTotal);
  const previewsCreditTotal = useSelector(fromOrder.selectCreditTotal);
  const orderResponse = useSelector(fromOrder.selectOrderResponse);
  const checkoutError = useSelector(fromOrder.selectCheckoutError);
  const errors = useSelector(fromSubscription.selectErrors);
  const currentBillingInformation = useSelector(fromBillingInformation.selectOneByAccountId(
    Number(accountId),
  ));
  const isNotSyncedBillingInfo = useSelector(fromBillingInformation.isNotSyncedAddressByAccountId(
    Number(accountId),
  )) ?? false;
  const currentCard = useSelector(fromPaymentMethod.selectOneByAccountId(Number(accountId)));
  const selectedPaymentMethod = useSelector(fromPaymentMethod.selectSelectedPaymentMethod);
  const [checkoutStatus, setCheckoutStatus] = useState<'loading' | 'finished' | undefined>(undefined);
  const [orderConfirmationKey, setOrderConfirmationKey] = useState(1);
  const { replace, replaceQuery, redirect } = useNavigate();
  const isExceededMaxAmount = () => previewsTotal > MAX_TOTAL_AMOUNT;
  const successEmail = <a target="_blank" href="mailto: success@katalon.com" rel="noreferrer">success@katalon.com</a>;
  const terminatedMonthlyProducts = new Set(get('terminated')?.split(',') ?? []);
  const subTotal = previewsNetTotal - previewsCreditTotal;
  const getTotalPrice = () => subTotal + previewsTotalTax - previewsDiscountTotal;

  const saveCardBtnEnabled = useSelector(fromOrder.selectSaveCardAndBillingInfoBtnEnabled);
  const isCardEditing = useSelector(fromPaymentMethod.isCardEditing);
  const cardInformationInvalid = useSelector(fromPaymentMethod.isCardInformationInvalid);
  const isEditBillingInfo = useSelector(fromBillingInformation.isEditBillingInfo);
  const isBillingInfoValid = useSelector(fromBillingInformation.isBillingInfoValid);
  const currentSubscriptions = useSelector(fromSubscription.selectByAccountId(accountId));
  const isOptedInFirstThreeLicenses = useSelector(fromAccounts.selectIsOptedInFirstThreeLicenses);
  const [showOptInFirstThreeLicenses, setShowOptInFirstThreeLicenses] = useState(false);
  const termsAndConditionsAccepted = useSelector(fromOrder.selectTermsAndConditionsAccepted);
  const isReadOnly = useReadOnlyMode();

  const [cookies] = useCookies();
  const cartSnapshot = usePlanCartSnapshot();
  const trackingCookies = useGetTrackingCookies();
  const features = createFeatures(flags);

  const featuresQuotaAndCycle = useGetQuotaAndCycleFromPathParams(features.map(it => it.feature));

  const buildOrderPreviewRequest = () => {
    const singleProductList = features
      .map(({ feature }) => ({
        productCode: getPlanId(
          feature,
          PlanInterval.fromString(featuresQuotaAndCycle[feature]?.cycle ?? 'YEAR'),
        ),
        quantity: featuresQuotaAndCycle[feature]?.quota ?? 0,
      }))
      .filter(item => item.quantity > 0);

    return {
      accountId,
      singleProductList,
    };
  };

  const hasMonthlyItem = features.some(
    ({ feature }) => featuresQuotaAndCycle[feature]?.cycle === PlanInterval.MONTH,
  );

  const account = useSelector(fromAccounts.selectAccountById(accountId));

  useEffect(() => {
    if (loading) return;

    if (isExceededMaxAmount()) {
      enqueueSnackbar(
        intl.formatMessage(
          { id: 'payment_status.total.exceeded.limit' },
          { email: successEmail },
        ),
        { variant: 'error' },
      );
    }
  }, [previewsTotal]);

  useEffect(() => {
    const redirectToPaymentStatusPage = () => {
      const invoiceNumbers = orderResponse?.singleProductInvoices;
      if (!invoiceNumbers?.length) {
        enqueueSnackbar(
          errors?.[0]?.message
            ? formatMessageRecurly(errors[0].message)
            : intl.formatMessage({ id: 'error.unknown' }),
          { variant: 'error' },
        );
        return;
      }
      replace(paymentStatus.path, replaceQuery({
        accountId,
        invoiceNumber: uniq(invoiceNumbers),
      }));
    };

    const doCheckout = async () => {
      const partnerId = cookies[PARTNER_ID_COOKIE_NAME];

      await dispatch(fromOrder.doCheckout(
        {
          singleProductList: buildOrderPreviewRequest().singleProductList,
          paymentMethod: selectedPaymentMethod,
          ...(partnerId && { partnerId }),
        },
      ));
      setCheckoutStatus('finished');
    };

    const redirectToSubscriptionManagementPage = () => {
      replace(plan.path, replaceQuery({
        accountId,
        showInstructionPopup: true,
      }));
    };

    if (checkoutStatus === 'loading' && currentBillingInformation
      && (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER
        || !isNotUsingStripe || currentCard)
    ) {
      doCheckout().finally(() => setLoading(false));
    }

    if (checkoutStatus === 'finished') {
      if (selectedPaymentMethod === PaymentMethodType.CREDIT_CARD) {
        const isPendingOrder = orderResponse?.status === OrderStatus.PENDING;
        const shouldConfirmPayment = isPendingOrder
          && orderResponse.stripeClientSecret
          && flags?.usingStripeSetupIntent;
        const shouldRedirectToStripeCheckout = isPendingOrder
          && orderResponse.stripeInvoiceUrl
          && flags?.usingStripeCheckoutSession;

        if (shouldRedirectToStripeCheckout) {
          redirect(orderResponse.stripeInvoiceUrl);
        } else if (shouldConfirmPayment) {
          replace(confirmPayment.path, replaceQuery({
            accountId,
            orderId: orderResponse.id.toString(),
          }));
        } else {
          redirectToPaymentStatusPage();
        }
      }

      if (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER) {
        redirectToSubscriptionManagementPage();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkoutStatus, currentBillingInformation, currentCard]);

  useEffect(() => {
    // Reload this section when billing information is updated
    setOrderConfirmationKey(window.crypto.getRandomValues(new Uint8Array(256))[0]);
  }, [currentBillingInformation]);

  useEffect(() => {
    // Temporary rem these code since it used for starter package
    // TODO: review it and decide to keep or remove
    // if (isInPromotionDomainExclusion(account?.ownerEmail, flags)) {
    //   replace(subscription.path, replaceQuery({
    //     accountId,
    //   }));
    // }
    const fetchInfo = async () => {
      setLoading(true);
      if (!accountId) return;
      const orderRequest = buildOrderPreviewRequest();
      await Promise.all([
        dispatch(fromOrder.doPreviewOrder(orderRequest)),
      ]);
    };
    fetchInfo().then(() => setLoading(false));
  }, [account, currentBillingInformation]);

  const sendCheckoutClickTracking = ({
    error,
    transactionId,
    invoiceNumbers,
  }: {
    error?: string;
    transactionId?: string | number;
    invoiceNumbers?: string[];
  } = {}) => {
    sendTrackingData(
      'checkout_page_all_action',
      accountId,
      undefined,
      withCartSnapshot({
        field: 'order_confirmation',
        object: 'button',
        action: 'checkout',
        is_required: false,
        is_valid: !error,
        error,
        ...trackingCookies,
        ...(transactionId ? { transaction_id: transactionId } : {}),
        ...(invoiceNumbers && invoiceNumbers.length > 0 ? { invoice_numbers: invoiceNumbers } : {}),
      }, cartSnapshot),
    );
  };

  useEffect(() => {
    if (checkoutStatus !== 'finished') {
      // Action doCheckout - not dispatch yet
      return;
    }
    const hasCheckoutResult = !!checkoutError || !!orderResponse;
    if (!hasCheckoutResult) {
      return;
    }

    const isSuccessCheckout = !!orderResponse;
    if (isSuccessCheckout) {
      const invoiceNumbers = orderResponse?.singleProductInvoices;
      const transactionId = orderResponse?.id;

      sendCheckoutClickTracking({
        transactionId,
        invoiceNumbers,
      });
    } else {
      sendCheckoutClickTracking({
        error: checkoutError?.message,
      });
    }
  }, [checkoutError, orderResponse, checkoutStatus]);

  const handleCheckout = () => {
    const checkout = async () => {
      setLoading(true);
      if (saveCardBtnEnabled) {
        setCheckoutStatus('loading');
      } else {
        await handleSave()
          .then(() => {
            dispatch(fromBillingInformation.setEditBillingInfo(false));
          })
          .catch(() => {
            setLoading(false);
          })
          .finally(() => {
            setCheckoutStatus('loading');
          });
      }
    };

    checkout().catch(() => {
      // do nothing
    });
  };

  useEffect(() => {
    setShowOptInFirstThreeLicenses(isOptedInFirstThreeLicenses);
  }, [isOptedInFirstThreeLicenses]);

  const isTerminated = (
    feature: Feature,
  ): boolean => {
    const featureQuotaAndCycle = featuresQuotaAndCycle[feature];
    const subscription = currentSubscriptions.find(it => it.feature === feature);

    const isMonthlyRecurly = subscription?.billingInterval === PlanInterval.MONTH
      && subscription?.source === SubscriptionSource.RECURLY;

    const isTerminatedByProduct = terminatedMonthlyProducts.has(feature)
      || (!!featureQuotaAndCycle?.cycle && featureQuotaAndCycle?.cycle !== PlanInterval.MONTH);

    return isMonthlyRecurly && isTerminatedByProduct;
  };

  const [hasTerminatedProduct, setHasTerminatedProduct] = useState(false);

  useEffect(() => {
    setHasTerminatedProduct(currentSubscriptions.some(sub => isTerminated(sub.feature)));
  }, [currentSubscriptions]);

  useCheckDowngrade(
    currentSubscriptions,
    featuresQuotaAndCycle,
  );

  const renderOrderItems = () => (
    <div id="order-confirmation" key={orderConfirmationKey}>
      {features.map(({
        key,
        feature,
        planName,
        quotaType,
        annualPrice,
        monthlyPrice,
      }) => {
        const cycle = featuresQuotaAndCycle[feature]?.cycle;
        return (
          cycle && (
            <OrderItem
              key={key}
              chosenInterval={cycle}
              feature={feature}
              chosenQuota={featuresQuotaAndCycle[feature]?.quota ?? 0}
              unitPrice={cycle === 'YEAR' ? annualPrice : monthlyPrice}
              planName={planName}
              quotaType={quotaType}
            />
          )
        );
      })}
    </div>
  );

  const renderMemo = () => (
    <Box id="memo">
      <Typography className={classes.memoTitle}>
        <MemoIcon />
        <span className={classes.memoTitleText}>
          <FormattedMessage id="checkout.memo" />
        </span>
      </Typography>
      <p className={classes.memoText}>
        <FormattedMessage id="checkout.memo.first" />
      </p>
      <p className={classes.memoText}>
        <FormattedMessage id="checkout.memo.second" />
      </p>
    </Box>
  );

  const renderTerminatedCreditsSection = () => (
    <div id="order-confirmation-credits" className={classes.creditItemContainer}>
      <Typography
        variant="body1"
        fontWeight={600}
        fontSize={21}
      >
        <FormattedMessage id="checkout.creditsSection.header" />
      </Typography>
      {features.map(({ key, feature, planName, quotaType }) => (
        featuresQuotaAndCycle[feature]?.cycle && isTerminated(feature) && (
          <CreditItem
            key={key}
            currentSubscription={currentSubscriptions.find(it => it.feature === feature)}
            feature={feature}
            planName={planName}
            quotaType={quotaType}
          />
        )
      ))}
    </div>
  );

  const isNotUsingStripe = !flags?.usingStripeCheckoutSession && !flags?.usingStripeSetupIntent;

  const checkoutDisabled = () => {
    if (isNotSyncedBillingInfo) {
      return true;
    }

    if (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER) {
      if (saveCardBtnEnabled && isEditBillingInfo) {
        return true;
      }
      return !currentBillingInformation;
    }

    // when show save button
    if (saveCardBtnEnabled) {
      // disable the checkout button when in editing mode
      if (isEditBillingInfo || (isCardEditing && isNotUsingStripe)) {
        return true;
      }

      // otw, disabled checkout button when no billing information
      // and no credit card (when default payment method is CC)
      return !currentBillingInformation
        || (isNotUsingStripe
          && selectedPaymentMethod === PaymentMethodType.CREDIT_CARD && !currentCard);
    }

    // disabled checkout button
    // when billing info not in editing mode and not have current billing information
    if (!isEditBillingInfo && !currentBillingInformation) {
      return true;
    }

    // disabled checkout button
    // when card info not in editing mode and not have a current card
    if (isNotUsingStripe
      && selectedPaymentMethod === PaymentMethodType.CREDIT_CARD
      && !isCardEditing && !currentCard) {
      return true;
    }

    // otw, check input info is valid (when not in show save button mode)
    return !isBillingInfoValid || cardInformationInvalid;
  };

  const disableCheckoutButton = loading || previewLoading
    || (previews.length === 0)
    || isExceededMaxAmount()
    || !termsAndConditionsAccepted // Terms and conditions are not accepted
    || (selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER && hasMonthlyItem)
    || checkoutDisabled()
    || isReadOnly;

  const renderCheckoutButton = () => (
    <Box
      id="checkout-button-container"
      display="flex"
    >
      <Button
        id="checkout-btn"
        variant="contained"
        onClick={handleCheckout}
        sx={theme => ({
          height: theme.spacing(5),
          backgroundColor: '#5959EB',
          color: '#fff',
          '&:hover': {
            backgroundColor: '#43509B',
          },
        })}
        disabled={disableCheckoutButton}
        fullWidth
      >
        {
          selectedPaymentMethod === PaymentMethodType.CREDIT_CARD
            ? intl.formatMessage({ id: 'billinginfo.checkout' })
            : intl.formatMessage({ id: 'bank.transfer.place_order' })
        }
      </Button>
    </Box>
  );

  const renderAddressSyncWarning = () => isNotSyncedBillingInfo && (
    <Box
      id="billing-info-warning"
      alignItems="center"
      alignContent="center"
      justifyContent="center"
      paddingTop={2}
    >
      <Alert
        id="expand-plan-price-summary-checkout-button-alert"
        severity="warning"
        sx={theme => ({
          marginBottom: 2,
          width: '100%',
          '& .MuiAlert-icon': {
            margin: theme.spacing(1, 2, 1, 0),
          },
        })}
      >
        <FormattedMessage id="billinginfo.billingcontact.not.synced" />
      </Alert>
    </Box>
  );

  const backToSelectProduct = () => {
    replace(
      plan.path,
      replaceQuery(parse(queryString()) as QueryDictionary),
    );
  };

  return (
    <>
      <Box
        id="purchase-details-section"
        width="100%"
        height="100%"
        display="flex"
        flexDirection="column"
        paddingLeft={theme => theme.spacing(4)}
        paddingTop={theme => theme.spacing(1)}
      >
        <Box
          id="purchase-details-header"
          display="flex"
          flexDirection="row"
          alignContent="center"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography
            variant="body1"
            fontWeight={600}
            fontSize={21}
          >
            <FormattedMessage id="checkout.orderconfirmation" />
          </Typography>
          <Button
            variant="text"
            onClick={backToSelectProduct}
            sx={{
              '&:hover': {
                backgroundColor: 'transparent',
              },
            }}
          >
            <Typography
              variant="body1"
              fontWeight={500}
              fontSize={18}
              color="#5959EB"
              sx={{
                textDecoration: 'underline',
              }}
            >
              <FormattedMessage id="billinginfo.billingcontact.button.edit.cart" />
            </Typography>
          </Button>
        </Box>
        <Box
          id="purchase-details-body"
          display="flex"
          flexDirection="column"
          alignContent="center"
          alignItems="center"
          justifyContent="space-between"
          width="100%"
          height="100%"
        >
          <Box
            id="purchase-item-details"
            display="flex"
            width="100%"
            height="100%"
            overflow="auto"
            paddingY={1}
            paddingX={2}
            minHeight={theme => theme.spacing(10)}
          >
            {previewLoading
              ? (<LoadingProgress />)
              : (
                <Box
                  id="purchase-details-content"
                  display="flex"
                  flexDirection="column"
                  width="100%"
                  height="100%"
                >
                  {renderOrderItems()}
                  {hasTerminatedProduct && renderTerminatedCreditsSection()}
                </Box>
              )}
          </Box>
          <Box
            id="expand-plan-price-summary-price"
            display="flex"
            width="100%"
            flexDirection="column"
          >
            <Divider />
            {/* Pricing summary */}
            <PricingSection
              subTotal={subTotal}
              totalDiscount={previewsDiscountTotal}
              totalTax={previewsTotalTax}
              totalPrice={getTotalPrice()}
              showOptInFirstThreeLicenses={showOptInFirstThreeLicenses}
              showTax
              loading={previewLoading}
            />
            <Divider />
            {/* Bank transfer memo section  */}
            {selectedPaymentMethod === PaymentMethodType.BANK_TRANSFER
              && (
                <>
                  <Divider />
                  {renderMemo()}
                </>
              )}

            {/* Address un-sync warning */}
            {renderAddressSyncWarning()}

            {/* Term and condition */}
            <TermAndConditionCheck />

            {/* Checkout button */}
            {renderCheckoutButton()}
          </Box>
        </Box>
      </Box>
      {!flags?.usingStripeCheckoutSession && (
        <ConfirmPaymentDialog />
      )}
    </>
  );
};

export default OrderConfirmationSection;
